import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calculator, Search, TrendingUp, Star } from 'lucide-react';
import { miniSimulators } from '../data/mock';
import SEO, { breadcrumbJsonLd } from '../components/SEO';

const SimulatorsListPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', ...new Set(miniSimulators.map(sim => sim.category))];
  
  const filteredSimulators = miniSimulators.filter(simulator => {
    const matchesSearch = !searchQuery || 
      simulator.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      simulator.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      simulator.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || simulator.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const popularSimulators = filteredSimulators.filter(sim => sim.popular);
  const otherSimulators = filteredSimulators.filter(sim => !sim.popular);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <SEO
        title="15 simulateurs gratuits freelance 2026"
        description="Simulateurs ultra-précis pour freelances : revenu net, charges URSSAF, TVA, IS vs IR, dividendes, ACRE, ARE, statut juridique. Données fiscales 2026."
        path="/simulateurs"
        jsonLd={breadcrumbJsonLd([
          { name: 'Accueil', path: '/' },
          { name: 'Simulateurs', path: '/simulateurs' }
        ])}
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Simulateurs gratuits
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              15 simulateurs ultra-précis pour optimiser votre création d'entreprise. 
              Données fiscales 2026 certifiées.
            </p>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Rechercher un simulateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="capitalize"
                >
                  {category === 'all' ? 'Tous' : category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Simulateurs populaires */}
        {popularSimulators.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <Star className="h-6 w-6 text-yellow-500" />
              <h2 className="text-2xl font-bold text-gray-900">Simulateurs populaires</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {popularSimulators.map((simulator) => (
                <Card key={simulator.id} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white border-0">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-100 flex items-center justify-center">
                        <span className="text-2xl">{simulator.icon}</span>
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg font-bold text-gray-900">
                          {simulator.title}
                        </CardTitle>
                        <Badge variant="secondary" className="mt-1 text-xs">
                          {simulator.category}
                        </Badge>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                        🔥 Populaire
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {simulator.description}
                    </p>
                    
                    <Link to={`/simulateur/${simulator.id}`}>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold group">
                        <Calculator className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                        Simuler maintenant
                        <TrendingUp className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Tous les simulateurs */}
        {otherSimulators.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {popularSimulators.length > 0 ? 'Autres simulateurs' : 'Tous les simulateurs'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {otherSimulators.map((simulator) => (
                <Card key={simulator.id} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center">
                        <span className="text-2xl">{simulator.icon}</span>
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg font-bold text-gray-900">
                          {simulator.title}
                        </CardTitle>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {simulator.category}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <p className="text-gray-600 mb-6">{simulator.description}</p>
                    
                    <Link to={`/simulateur/${simulator.id}`}>
                      <Button variant="outline" className="w-full border-2 hover:bg-blue-50 hover:border-blue-500">
                        <Calculator className="h-4 w-4 mr-2" />
                        Utiliser ce simulateur
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Aucun résultat */}
        {filteredSimulators.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Calculator className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucun simulateur trouvé
            </h3>
            <p className="text-gray-600 mb-4">
              Essayez avec d'autres mots-clés ou changez de catégorie
            </p>
            <Button 
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              variant="outline"
            >
              Voir tous les simulateurs
            </Button>
          </div>
        )}

        {/* Call to action */}
        <div className="mt-16 text-center bg-blue-600 rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-4">
            Besoin d'un accompagnement personnalisé ?
          </h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Nos simulateurs vous donnent une première estimation. Pour une analyse complète 
            et personnalisée de votre situation, utilisez notre outil complet.
          </p>
          <Button 
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold"
            onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
          >
            Accéder au simulateur complet
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SimulatorsListPage;